import { ChangeDetectorRef, Component, Inject, Input, ViewChild } from '@angular/core';
import { ActionsHeaderComponent, SubMenuTypes } from "./components/actions-header/actions-header.component";
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { FiscalDataSubmenuComponent } from './components/fiscal-data-submenu/fiscal-data-submenu.component';
import { ContactInformationSubmenuComponent } from './components/contact-information-submenu/contact-information-submenu.component';
import { ProfileDataSubmenuComponent } from './components/profile-data-submenu/profile-data-submenu.component';
import { RolesPermissionsSubmenuComponent } from './components/roles-permissions-submenu/roles-permissions-submenu.component';
import { MaterialModule } from '../../../shared/modules/material/material.module';
import { FormGroup } from '@angular/forms';
import { UserService } from '../../../core/services/user/user.service';
import { ActionContext, ContactDetails, FiscalData, PermissionUser, RolUser, UserCreate, UserDetail, UserUpdate } from '../../../core/models/user.models';
import { PlatformServices } from '../../../core/services/platform/platform.service';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../../core/services/loader/loader.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UtilsService } from '../../../shared/services/utils.service';
import { concatMap } from 'rxjs';

@Component({
  selector: 'app-actions-dialog',
  standalone: true,
  imports: [CommonModule, ActionsHeaderComponent, TranslateModule, FiscalDataSubmenuComponent, ContactInformationSubmenuComponent,
    ProfileDataSubmenuComponent, RolesPermissionsSubmenuComponent, FontAwesomeModule, MaterialModule
  ],
  templateUrl: './actions-dialog.component.html',
  styleUrl: './actions-dialog.component.scss'
})
export class ActionsDialogComponent {
  @ViewChild(ActionsHeaderComponent, { read: ActionsHeaderComponent, static: false }) actionsHeader!: ActionsHeaderComponent;
  @ViewChild(ProfileDataSubmenuComponent, { read: ProfileDataSubmenuComponent, static: false }) profileSubMenu!: ProfileDataSubmenuComponent;
  @ViewChild(ContactInformationSubmenuComponent, { read: ContactInformationSubmenuComponent, static: false }) contactInformationSubmenu!: ContactInformationSubmenuComponent;
  @ViewChild(FiscalDataSubmenuComponent, { read: FiscalDataSubmenuComponent, static: false }) fiscalDataSubMenu!: FiscalDataSubmenuComponent;
  @ViewChild(RolesPermissionsSubmenuComponent, { read: RolesPermissionsSubmenuComponent, static: false }) rolesAndPermissionsSubMenu!: RolesPermissionsSubmenuComponent;

  @Input() mode: "create" | "update" = "create";

  subMenuSelected!: SubMenuTypes;
  lastSubMenuSelected!: SubMenuTypes;
  subMenuTypesEnum = SubMenuTypes;
  subMenuFormGroup!: FormGroup;

  isActive!: boolean;
  isAdmin!: boolean;
  context!: ActionContext;
  contactDetails!: ContactDetails[];
  fiscalData!: FiscalData | null;
  roles!: RolUser[];
  permissionsWithoutRol!: PermissionUser[];

  hasChanges: boolean = false;
  subMenuChanged: boolean = false;
  isFirstLoad: boolean = true;
  currentScreen: string;

  constructor(private cdr: ChangeDetectorRef, private userService: UserService, private platformService: PlatformServices,
    private loaderService: LoaderService, private utilityService: UtilsService,
    public dialogRef: MatDialogRef<ActionsDialogComponent>, public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { isActive: boolean, user: UserDetail, mode: "create" | "update", 
      context: ActionContext }) {
    if (this.data.user) {
      this.isAdmin = this.data.user.admin ?? false;
      this.contactDetails = this.data.user?.contactDetails ?? [];
      this.fiscalData = this.data.user?.fiscalData ?? null;
      this.roles = this.data.user?.roles ?? [];
      this.permissionsWithoutRol = this.data.user?.permissions ?? [];
    }

    this.isActive = this.data.isActive;

    this.mode = this.data.mode;
    this.context = this.data.context;
    this.currentScreen = this.platformService.currentScreenSize;
  }

  ngOnInit() {
    this.subMenuSelected = this.subMenuSelected ?? this.subMenuTypesEnum.PROFILE_DATA;
    this.lastSubMenuSelected = this.subMenuSelected;
  }

  ngAfterViewInit() {
    this.changeFormGroup();
    this.cdr.detectChanges();
  }

  changeSubMenu(subMenu: SubMenuTypes) {
    this.lastSubMenuSelected = this.subMenuSelected;
    this.subMenuSelected = subMenu;
    if (this.subMenuFormGroup) {
      if (this.hasChanges) {
        this.subMenuChanged = true;
      } else {
        setTimeout(() => {
          this.changeFormGroup();
        }, 100);
      }

    }
  }

  changeActiveState(isActive: boolean) {
    this.isActive = isActive;
    this.hasChanges = this.checkChanges();
  }

  save(isDiscardModal?: boolean) {
    const formValues = this.subMenuFormGroup.value;

    this.loaderService.showLoader();
    this.hasChanges = false;

    //If the event is triggered in the 'discard modal', the formGroup of the last selected subMenu is taken,
    // otherwise the current one is taken
    const conditionToSave = isDiscardModal ? this.lastSubMenuSelected : this.subMenuSelected;
    switch (conditionToSave) {
      case this.subMenuTypesEnum.PROFILE_DATA: {

        if (this.data.user) {
          const hasUpdateOauth = (this.data.user.active !== this.isActive) || !!formValues.password;

          const userUpdate: UserUpdate = {
            active: this.isActive,
            admin: formValues.admin,
            category: formValues.category[0],
            externalId: formValues.externalId,
            firstName: formValues.firstName,
            langId: formValues.langId[0],
            lastName: formValues.lastName,
          }

          if (formValues?.password) {
            userUpdate.password = formValues.password;
          }
          if (formValues?.pronounType[0]) {
            userUpdate.pronounType = formValues?.pronounType[0];
          }
          if (formValues?.specialityId[0]) {
            userUpdate.specialityId = Number(formValues?.specialityId[0]);
          }
          if (formValues?.category[0]) {
            userUpdate.category = formValues?.category[0];
          }
          if (formValues.langId[0]) {
            userUpdate.langId = formValues.langId[0];
          }
          if (formValues?.externalId) {
            userUpdate.externalId = formValues?.externalId;
          }
          this.userService.update(userUpdate, this.data.user.id, hasUpdateOauth).subscribe({
            next: (response: UserDetail) => {
              this.data.user.active = response.active;
              this.data.user.admin = response.admin;
              this.data.user.externalId = response.externalId;
              this.data.user.firstName = response.firstName;
              this.data.user.id = response.id;
              this.data.user.langId = response.langId;
              this.data.user.lastName = response.lastName;
              this.data.user.pronounType = response.pronounType;
              this.data.user.specialityId = response.specialityId;
              this.data.user.userName = response.userName;

              this.subMenuChanged = false;
              this.hasChanges = false;
              this.isAdmin = !!response.admin;
              
              setTimeout(() => {
                this.changeFormGroup();
              }, 200);
            },
            error: (error) => {
              this.loaderService.hideLoader();
            }
          });

        } else {
          let userCreate: UserCreate = {
            active: this.isActive,
            admin: formValues.admin,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            password: formValues.password,
            username: formValues.username,
          }
          if (formValues?.pronounType[0]) {
            userCreate.pronounType = formValues?.pronounType[0];
          }
          if (formValues?.specialityId[0]) {
            userCreate.specialityId = Number(formValues?.specialityId[0]);
          }
          if (formValues?.category[0]) {
            userCreate.category = formValues?.category[0];
          }
          if (formValues.langId[0]) {
            userCreate.langId = formValues.langId[0];
          }
          if (formValues?.externalId) {
            userCreate.externalId = formValues?.externalId;
          }
          this.userService.create(userCreate).subscribe({
            next: (user: UserDetail) => {
              this.data.user = user;
              this.contactDetails = this.data.user?.contactDetails ?? [];
              this.fiscalData = this.data.user?.fiscalData ?? null;
              this.roles = this.data.user?.roles ?? [];
              this.permissionsWithoutRol = this.data.user?.permissions ?? [];

              this.isAdmin = !!user.admin;
              this.loaderService.hideLoader();
            },
            error: (error) => {
              this.loaderService.hideLoader();
            }
          });

        }

        break;
      }
      case this.subMenuTypesEnum.CONTACT_INFORMATION: {
        const contactsDetailsDtos = formValues.contactTypesRows.map((contact: any) => {
          const { contactType, ...contactDTO } = contact
          return {
            active: this.isActive,
            ...contactDTO,
            contactType: contactType[0]
          }
        })
        const contactsRequest = {
          admin: this.isAdmin,
          contactDetails: contactsDetailsDtos
        }
        this.userService.update(contactsRequest, this.data.user.id, !(this.data.user.active === this.isActive)).subscribe({
          next: (response: UserDetail) => {
            this.data.user.contactDetails = response.contactDetails;
            this.contactDetails = response.contactDetails ?? [];

            this.subMenuChanged = false;
            this.hasChanges = false;

            setTimeout(() => {
              this.changeFormGroup();
            }, 200);
          },
          error: (error) => {
            this.loaderService.hideLoader();
          }

        })
        break;
      }
      case this.subMenuTypesEnum.FISCAL_DATA: {
        const { country, documentType, countryDivition, destination, ...fiscalDataDTO } = formValues;
        
        let fiscalDataRequest = {
          admin: this.isAdmin,
          active: this.isActive,
          fiscalData: {
            ...fiscalDataDTO,
            documentType: documentType[0]
          }

        }

        if(country.length) {
          fiscalDataRequest.fiscalData.countryId = country[0];
        }
        if(countryDivition.length) {
          fiscalDataRequest.fiscalData.countryDivisionId = countryDivition[0];
        }
        if(destination.length) {
          fiscalDataRequest.fiscalData.destinationId = destination[0];
        }

        this.userService.update(fiscalDataRequest, this.data.user.id, !(this.data.user.active === this.isActive)).subscribe({
          next: (response: UserDetail) => {
            this.data.user.fiscalData = response.fiscalData;
            this.fiscalData = response.fiscalData ?? null;

            this.subMenuChanged = false;
            this.hasChanges = false;

            setTimeout(() => {
              this.changeFormGroup();
            }, 200);
          },
          error: (error) => {
            this.loaderService.hideLoader();
          }
        })
        break;
      }
      case this.subMenuTypesEnum.ROLES_AND_PERMISSIONS: {
        formValues['admin'] = this.isAdmin;
        this.userService.update(formValues, this.data.user.id, !(this.data.user.active === this.isActive)).pipe(
          concatMap((response: UserDetail) => {

            this.subMenuChanged = false;
            this.hasChanges = false;

            setTimeout(() => {
              this.changeFormGroup();
            }, 200);

            return this.userService.getById(this.data.user.id);
          })
        ).subscribe({
          next: (userDetail: UserDetail) => {
            this.data.user.roles = userDetail.roles;
            this.roles = userDetail.roles as RolUser[];
            this.permissionsWithoutRol = userDetail.permissions as PermissionUser[];
          },
          error: (error) => {
            this.loaderService.hideLoader();
          }
        })

        break;
      }
    }

  }

  changeFormGroup() {
    //Condition added to not overlap the first load of the configuration-profile loader
    if(!this.isFirstLoad) {
      this.loaderService.showLoader();
    }
    this.isFirstLoad = false;

    switch (this.subMenuSelected) {
      case this.subMenuTypesEnum.PROFILE_DATA: {
        this.subMenuFormGroup = this.profileSubMenu.formGroup;

        break;
      }
      case this.subMenuTypesEnum.CONTACT_INFORMATION: {
        this.subMenuFormGroup = this.contactInformationSubmenu.formGroup;

        break;
      }
      case this.subMenuTypesEnum.FISCAL_DATA: {
        this.subMenuFormGroup = this.fiscalDataSubMenu.formGroup;

        break;

      }
      case this.subMenuTypesEnum.ROLES_AND_PERMISSIONS: {
        this.subMenuFormGroup = this.rolesAndPermissionsSubMenu.formGroup;

        break;
      }
    }

    setTimeout(() => {
      this.loaderService.hideLoader();   
    }, 1000);

    this.subMenuFormGroup.valueChanges.subscribe((response: any) => {
      if (this.subMenuSelected === this.subMenuTypesEnum.PROFILE_DATA) {
        this.isAdmin = response.admin;
      }
      this.hasChanges = this.checkChanges();
    })
  }

  checkChanges() {
    const formValues = this.subMenuFormGroup.value;
    const userData = this.data.user;

    if (this.data.user && userData.active !== this.isActive) {
      return true;
    }

    switch (this.subMenuSelected) {
      case this.subMenuTypesEnum.PROFILE_DATA: {
        const userOldData: UserUpdate = {
          active: userData?.active ?? false,
          admin: userData?.admin ?? false,
          firstName: userData?.firstName,
          lastName: userData?.lastName,
          externalId: userData?.externalId,
          pronounType: userData?.pronounType,
          langId: userData?.langId,
          specialityId: userData?.specialityId,
          category: userData?.category
        }

        const userCurrentData: UserUpdate = {
          active: this.isActive,
          admin: formValues.admin,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          externalId: formValues.externalId
        }

        if (formValues.password) {
          userCurrentData.password = formValues.password;
        }
        if (formValues.pronounType?.length) {
          userCurrentData.pronounType = formValues.pronounType[0];
        }
        if (formValues.langId?.length) {
          userCurrentData.langId = formValues.langId[0];
        }
        if (formValues.specialityId?.length) {
          userCurrentData.specialityId = Number(formValues.specialityId[0]);
        }
        if (formValues.category?.length) {
          userCurrentData.category = formValues.category[0];
        }

        return !this.utilityService.isObjectsEquals(userCurrentData, userOldData);
      }
      case this.subMenuTypesEnum.CONTACT_INFORMATION: {

        let userCurrentData = formValues.contactTypesRows ?? [];
        let userOldData = userData.contactDetails ?? [];

        if(userOldData.length) {
          userOldData = userOldData.map((contact: ContactDetails) => {
            delete contact.id;
            delete contact.userId;

            return contact;
          })
        }

        if(userCurrentData.length) {
          userCurrentData = userCurrentData.map((value: any) => {
            value.contactType = value.contactType[0];
            return value;
          })
        }

        return !this.utilityService.isArraysEquals(userCurrentData, userOldData);
      }
      case this.subMenuTypesEnum.FISCAL_DATA: {

        const userCurrentData = {
          documentType: formValues.documentType ? formValues.documentType[0] : null,
          documentNumber: formValues.documentNumber,
          fiscalName: formValues.fiscalName,
          address: formValues.address,
          countryId: formValues.country ? formValues.country[0] : null,
          countryDivitionId: formValues.countryDivition ? formValues.countryDivition[0] : null,
          destinationId: formValues.destination ? String(formValues.destination[0]) : null,
        }
        const userOldData = {
          documentType: userData.fiscalData?.documentType,
          documentNumber: userData.fiscalData?.documentNumber,
          fiscalName: userData.fiscalData?.fiscalName,
          address: userData.fiscalData?.address,
          countryId: userData.fiscalData?.countryId,
          countryDivitionId: userData.fiscalData?.countryDivisionId,
          destinationId: userData.fiscalData?.destinationId,
        }

        return !this.utilityService.isObjectsEquals(userCurrentData, userOldData);

      }
      case this.subMenuTypesEnum.ROLES_AND_PERMISSIONS: {
        const userCurrentRoles = formValues.roles;
        const userCurrentPermissions = formValues.permissions;
        const userOldRoles = userData.roles ?? [];
        const userOldPermissions = userData.permissions ?? [];
        const userOldPermissionsByRol = this.userService.getPermissionsByRoles(userOldRoles);
        const userOldPermissionsJoin = [...userOldPermissions, ...userOldPermissionsByRol];

        return !this.utilityService.isArraysEquals(userCurrentRoles, userOldRoles)
          || !this.utilityService.isArraysEquals(userCurrentPermissions, userOldPermissionsJoin)
      }
    }
  }

  discardChanges() {
    this.hasChanges = false;
    this.subMenuChanged = false;

    setTimeout(() => {
      this.actionsHeader.toggleSubMenu(this.subMenuSelected);
    }, 0);

  }

  closeDialog() {
    this.dialogRef.close();
  }

}
